/** A projectile pool is the live charged child of a projectile-producing spell. */
export interface ProjectilePool {
  remaining: number;
  total: number;
  instant?: InstantProjectileCast;
}

export interface InstantProjectileCast {
  parentId: string;
  variable1?: string;
  variable2?: string;
}

export function instantProjectileCount(itemType: unknown, read: (key: string) => unknown, childCharges: unknown, castLevel?: unknown): number | null {
  if (itemType !== 'spell' || read('spell_upcast_additional_projectiles_created') == null
    || read('range') !== 'D82855E1-4BB9-93EA-6374-B9904318BC1D') return null;
  if (['duration_type', 'effect_ends_on_start_of_caster_next_turn', 'effect_ends_on_end_of_caster_next_turn',
    'effect_ends_on_target_start_of_turn', 'persistent_aoe'].some(key => !!read(key))) return null;
  const base = Number(childCharges), level = Number(read('spell_level')) || 0;
  if (!Number.isInteger(base) || base < 1 || base > 1000) return null;
  const chosen = Number(castLevel);
  return Math.min(1000, base + (level > 0 && Number.isFinite(chosen) ? Math.max(0, Math.trunc(chosen) - level) : 0));
}

export interface ProjectileAssignment {
  targetSelection: string;
  propagation?: import('../../../ft3d/engine/ft3d-aoe-propagation.js').AoePropagation | null;
  /** Keep the banked stage after the placement controller retires its draft. */
  placementProof?: import('./ft3d-aoe-placement-contract').Ft3dAoePlacementStageEnvelope
    & import('./ft3d-aoe-placement-contract').Ft3dAoePlacementIntent;
}

export interface ProjectileAreaAssignment {
  targetId: string;
  propagation?: ProjectileAssignment['propagation'];
  placementProof?: ProjectileAssignment['placementProof'];
}

export function parseProjectilePool(value: unknown): ProjectilePool | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const remaining = Number(row.remaining), total = Number(row.total);
  return Number.isInteger(remaining) && remaining >= 0 && remaining <= 1000
    && Number.isInteger(total) && total >= remaining && total <= 1000 ? { remaining, total,
      ...(row.instant && typeof row.instant === 'object' && typeof (row.instant as InstantProjectileCast).parentId === 'string'
        ? { instant: { ...(row.instant as InstantProjectileCast) } } : {}) } : null;
}

export function projectileCursor(assigned: number, total: number, onTarget = 0): string {
  const label = `${Math.max(0, assigned)}/${total}${onTarget ? ` (${onTarget})` : ''}`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="116" height="48"><path d="M12 0v24M0 12h24" stroke="black" stroke-width="4"/><path d="M12 0v24M0 12h24" stroke="#ffd166" stroke-width="2"/><rect x="26" y="2" width="88" height="25" rx="5" fill="#151921"/><text x="31" y="20" fill="#ffd166" font-family="sans-serif" font-size="14">${label}</text></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 12 12, crosshair`;
}

declare global {
  interface Window {
    ftGetProjectilePool?: (casterId: string, abilityId: string) => ProjectilePool | null;
    ftResumeProjectileSpell?: (casterId: string, spellId: string, onlyIfIdle?: boolean) => boolean;
    ftUseProjectileAssignments?: (casterId: string, abilityId: string, assignments: ProjectileAssignment[], instant?: InstantProjectileCast) => Promise<unknown>;
  }
}
